import { useEffect, useState } from 'react';

import { CheckStatus, EntityRefUtil, formatDateWithUserTimezone, GetWorkOrdersResponseDTO, IssueStatus } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import WorkOrderDrawer from '@/components/awa/workorders/workorder-drawer';
import SearchIcon from '@/components/icons/search';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { getAWAWorkOrdersQueryOptions } from '@/queries/work-orders';
import { useUserStore } from '@/stores/user.store';

export default function WorkOrdersPage() {
  const { user } = useUserStore();
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<GetWorkOrdersResponseDTO['data'][number] | undefined>(undefined);

  const { data: workorders, isLoading } = useQuery(getAWAWorkOrdersQueryOptions({ user }));

  useEffect(() => {
    if (selectedWorkOrder && workorders) {
      const updatedWorkOrder = workorders.data.find((wo) => wo.id === selectedWorkOrder.id);
      setSelectedWorkOrder(updatedWorkOrder);
    }
  }, [workorders, selectedWorkOrder]);

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-[140px] w-full" />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4 p-4">
        <h1 className="text-center text-2xl font-medium">Work Orders</h1>
        {workorders?.data.length ? (
          workorders.data.map((wo) => {
            const isOverdue = wo.dueDate && dayjs(wo.dueDate).isBefore(dayjs().add(1, 'day'));
            const isDue = wo.dueDate && !isOverdue;
            const completed =
              (wo.issues?.filter((issue) => issue.status === IssueStatus.Closed).length || 0) +
              (wo.checks?.filter((check) => check.status === CheckStatus.Closed).length || 0);
            const total = (wo.issues?.length || 0) + (wo.checks?.filter((check) => check.status !== CheckStatus.Void).length || 0);

            return (
              <Card key={wo.id} className="hover:bg-grey-1 cursor-pointer overflow-hidden" onClick={() => setSelectedWorkOrder(wo)}>
                <div className="flex gap-4 p-4">
                  <div
                    className={cn('w-1 self-stretch rounded-full', {
                      'bg-default-red': isOverdue,
                      'bg-default-yellow': isDue,
                      'bg-default-green': !wo.dueDate,
                    })}
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium">{wo.asset ? EntityRefUtil.createAssetLabel(wo.asset) : ''}</h3>
                        <p className="text-secondary-text text-sm">{wo.asset?.location?.name}</p>
                      </div>
                      <span className="text-secondary-text text-xs">{EntityRefUtil.createWorkOrderRef(wo.number)}</span>
                    </div>

                    <div className="mt-2 space-y-1">
                      <p className="text-sm">
                        <span className="font-medium">{completed}</span> of <span className="font-medium">{total}</span> Task(s)
                      </p>
                      {wo.dueDate && <p className="text-sm">Due: {formatDateWithUserTimezone(new Date(wo.dueDate), user?.timezone)}</p>}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })
        ) : (
          <div className="text-secondary-text flex h-[200px] items-center justify-center">
            <div className="flex items-center gap-2">
              <SearchIcon className="h-5 w-5" />
              <span>You do not have any work orders assigned</span>
            </div>
          </div>
        )}
      </div>

      <WorkOrderDrawer workOrder={selectedWorkOrder} open={!!selectedWorkOrder} onOpenChange={(open) => !open && setSelectedWorkOrder(undefined)} />
    </>
  );
}
